import React, { useEffect, useState, useRef } from 'react';
import {
  collection,
  getDocs,
  doc,
  deleteDoc,
  setDoc,
  query,
  where,
  getDoc,
  updateDoc,
} from 'firebase/firestore';
import { createUserWithEmailAndPassword, getAuth } from 'firebase/auth';
import { initializeApp } from 'firebase/app';
import { firestore, auth } from '../../database/firebase';
import { motion, AnimatePresence } from 'framer-motion';
import {
  HiEye,
  HiTrash,
  HiPlus,
  HiXMark,
  HiUser,
  HiEnvelope,
  HiPhone,
  HiBuildingOffice2,
  HiCheckCircle,
  HiExclamationTriangle,
  HiInformationCircle,
  HiUserGroup,
  HiAcademicCap,
  HiLockClosed,
  HiShieldCheck,
  HiClipboardDocumentList,
  HiDocumentText,
  HiEllipsisVertical,
  HiLockOpen,
} from 'react-icons/hi2';
import ClientProfilePopover from '../features/ClientProfilePopover';
import ReportSubmissionForm from './ReportSubmissionForm';
import ClientReportsViewer from '../features/ClientReportsViewer';
import TransferModal from '../features/TransferModal';
import EntrepreneurDashboard from '../../components/entrepreneurProfile/EntrepreneurDashboard';
import { useAuth } from '../../context/AuthContext';
import { useUserRole } from '../../context/UserRoleContext';
import StageSelector from '../../components/ui/StageSelector';
import { BUSINESS_STAGE_COLORS, normalizeBusinessStage } from '../../constants/businessStages';
import toast from 'react-hot-toast';
import ConfirmationModal from '../../components/SharedFeature/ConfirmationModal';

const LoadingSpinner = () => (
  <motion.div
    className="flex flex-col items-center justify-center py-20"
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
  >
    <motion.div
      className="border-3 h-8 w-8 rounded-full border-blue-200 border-t-blue-600"
      animate={{ rotate: 360 }}
      transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
    />
    <p className="mt-4 text-sm text-gray-500">Loading clients...</p>
  </motion.div>
);

const Toast = ({ message, type, onClose }) => (
  <motion.div
    initial={{ opacity: 0, y: -50, scale: 0.95 }}
    animate={{ opacity: 1, y: 0, scale: 1 }}
    exit={{ opacity: 0, y: -50, scale: 0.95 }}
    className={`fixed right-4 top-4 z-50 flex items-center gap-3 rounded-xl px-4 py-3 shadow-lg ${
      type === 'success'
        ? 'border border-green-200 bg-green-50'
        : type === 'error'
          ? 'border border-red-200 bg-red-50'
          : 'border border-gray-200 bg-gray-50'
    }`}
  >
    {type === 'success' && <HiCheckCircle className="h-5 w-5 text-green-600" />}
    {type === 'error' && <HiExclamationTriangle className="h-5 w-5 text-red-600" />}
    {type === 'info' && <HiInformationCircle className="h-5 w-5 text-blue-600" />}
    <span
      className={`text-sm font-medium ${type === 'success' ? 'text-green-800' : type === 'error' ? 'text-red-800' : 'text-black'}`}
    >
      {message}
    </span>
    <button
      onClick={onClose}
      className={`ml-2 hover:opacity-70 ${type === 'success' ? 'text-green-600' : type === 'error' ? 'text-red-600' : 'text-gray-600'}`}
    >
      <HiXMark className="h-4 w-4" />
    </button>
  </motion.div>
);

const AddClientForm = ({ open, onClose, onSubmit, onError }) => {
  const [formData, setFormData] = useState({
    firstname: '',
    lastname: '',
    email: '',
    phone: '',
    organization: '',
    businessStage: '',
    cohortGroup: '',
  });
  const [loading, setLoading] = useState(false);
  const [moderators, setModerators] = useState([]);
  const [lastAssignedModeratorIndex, setLastAssignedModeratorIndex] = useState(0);

  useEffect(() => {
    if (open) {
      fetchActiveModerators();
      const savedIndex = localStorage.getItem('lastAssignedModeratorIndex');
      if (savedIndex) {
        setLastAssignedModeratorIndex(parseInt(savedIndex));
      }
    }
  }, [open]);

  const fetchActiveModerators = async () => {
    try {
      const snapshot = await getDocs(
        query(collection(firestore, 'moderator'), where('status', '==', 'active'))
      );
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setModerators(data);
    } catch (err) {
      toast.error('Failed to fetch moderators:', err);
    }
  };

  const getNextModerator = () => {
    if (moderators.length === 0) return null;

    const nextIndex = (lastAssignedModeratorIndex + 1) % moderators.length;
    setLastAssignedModeratorIndex(nextIndex);
    localStorage.setItem('lastAssignedModeratorIndex', nextIndex.toString());
    return moderators[nextIndex];
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setLoading(true);

    try {
      const defaultPassword = 'Password@12345';

      const assignedModerator = getNextModerator();

      if (!assignedModerator) {
        throw new Error('No active moderators available for assignment');
      }

      const tempApp = initializeApp(
        {
          apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
          authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
          projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
          storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
          messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
          appId: process.env.REACT_APP_FIREBASE_APP_ID,
        },
        'temp-app-' + Date.now()
      );

      const tempAuth = getAuth(tempApp);

      const userCredential = await createUserWithEmailAndPassword(
        tempAuth,
        formData.email,
        defaultPassword
      );

      const clientData = {
        ...formData,
        businessStage: normalizeBusinessStage(formData.businessStage),
        uid: userCredential.user.uid,
        assigned: assignedModerator.firstname + ' ' + assignedModerator.lastname,
        assignedEmail: assignedModerator.email,
        assignedCoachName: assignedModerator.firstname + ' ' + assignedModerator.lastname,
        assignedCoachEmail: assignedModerator.email,
        status: 'active',
        role: 'client',
        mustChangePassword: true,
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      await setDoc(doc(firestore, 'clients', userCredential.user.uid), clientData);

      await tempAuth.signOut();

      setFormData({
        firstname: '',
        lastname: '',
        email: '',
        phone: '',
        organization: '',
        businessStage: '',
        cohortGroup: '',
      });

      onSubmit({ success: true, password: defaultPassword, userId: userCredential.user.uid });
    } catch (error) {
      let errorMessage = 'Failed to create client';
      if (error.code === 'auth/email-already-in-use') {
        errorMessage = 'A user with this email already exists';
      } else if (error.code === 'auth/invalid-email') {
        errorMessage = 'Invalid email address';
      } else if (error.code === 'auth/weak-password') {
        errorMessage = 'Password is too weak';
      } else if (error.message) {
        errorMessage = error.message;
      }

      if (onError) {
        onError(new Error(errorMessage));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  if (!open) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
    >
      <motion.div
        initial={{ opacity: 0, y: 50, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 50, scale: 0.95 }}
        transition={{ duration: 0.2, ease: 'easeOut' }}
        className="relative w-full max-w-2xl overflow-y-auto rounded-2xl bg-white shadow-2xl"
      >
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100">
              <HiUser className="h-5 w-5 text-blue-600" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-black">Add New Client</h2>
              <p className="text-sm text-gray-500">Create a new client account</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
          >
            <HiXMark className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6 p-6">
          <div>
            <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide text-blue-900/80">
              Client Information
            </h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">First Name *</label>
                <input
                  type="text"
                  name="firstname"
                  value={formData.firstname}
                  onChange={handleInputChange}
                  required
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="Enter first name"
                />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Last Name *</label>
                <input
                  type="text"
                  name="lastname"
                  value={formData.lastname}
                  onChange={handleInputChange}
                  required
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="Enter last name"
                />
              </div>
            </div>
          </div>

          <div>
            <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide text-blue-900/80">
              Contact Information
            </h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Email *</label>
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  required
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="Enter email address"
                />
              </div>
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Phone *</label>
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  required
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="Enter phone number"
                />
              </div>
            </div>
          </div>

          <div>
            <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide text-blue-900/80">
              Business Information
            </h3>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  Organization *
                </label>
                <input
                  type="text"
                  name="organization"
                  value={formData.organization}
                  onChange={handleInputChange}
                  required
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                  placeholder="Enter organization name"
                />
              </div>
              <div className="md:col-span-2">
                <StageSelector
                  value={formData.businessStage}
                  onChange={v => setFormData(prev => ({ ...prev, businessStage: v }))}
                />
              </div>
            </div>
            <div className="mt-4">
              <label className="mb-2 block text-sm font-medium text-gray-700">Cohort Group</label>
              <input
                type="text"
                name="cohortGroup"
                value={formData.cohortGroup}
                onChange={handleInputChange}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="Enter cohort group (optional)"
              />
            </div>
          </div>

          <div className="rounded-lg border border-blue-100 bg-blue-50/60 p-4">
            <h3 className="mb-3 text-sm font-semibold uppercase tracking-wide text-blue-900/80">
              Auto-assigned Information
            </h3>
            <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <HiLockClosed className="h-4 w-4 text-blue-500" />
                <span>Default Password:</span>
                <span className="rounded bg-gray-100 px-2 py-1 font-mono text-xs">
                  Password@12345
                </span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <HiShieldCheck className="h-4 w-4 text-blue-500" />
                <span>Status:</span>
                <span className="font-medium text-green-600">Active</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <HiUserGroup className="h-4 w-4 text-blue-500" />
                <span>Role:</span>
                <span className="font-medium text-blue-600">Client</span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <HiAcademicCap className="h-4 w-4 text-blue-500" />
                <span>Business Advisor:</span>
                <span className="font-medium text-gray-800">
                  {moderators.length > 0 ? 'Auto-assigned' : 'No Business Advisors available'}
                </span>
              </div>
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <HiShieldCheck className="h-4 w-4 text-blue-500" />
                <span>UID Consistency:</span>
                <span className="font-medium text-green-600">Firebase Auth UID</span>
              </div>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex items-center justify-end gap-3 border-t border-gray-200 pt-6">
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {loading ? (
                <>
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                  Creating...
                </>
              ) : (
                <>
                  <HiPlus className="h-4 w-4" />
                  Create Client
                </>
              )}
            </button>
          </div>
        </form>
      </motion.div>
    </motion.div>
  );
};

const ClientsTable = () => {
  const { currentUser } = useAuth();
  const { userRole } = useUserRole();

  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showAddClientForm, setShowAddClientForm] = useState(false);
  const [toast, setToast] = useState(null);
  const [error, setError] = useState('');
  const [moderators, setModerators] = useState([]);
  const [lastAssignedModeratorIndex, setLastAssignedModeratorIndex] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');

  // Filter clients based on search term
  const filteredClients = clients.filter(client => {
    if (!searchTerm) return true;

    const searchLower = searchTerm.toLowerCase();
    return (
      client.firstname?.toLowerCase().includes(searchLower) ||
      client.lastname?.toLowerCase().includes(searchLower) ||
      client.email?.toLowerCase().includes(searchLower) ||
      client.organization?.toLowerCase().includes(searchLower) ||
      client.phone?.toLowerCase().includes(searchLower) ||
      `${client.firstname || ''} ${client.lastname || ''}`.toLowerCase().includes(searchLower) ||
      client.businessStage?.toLowerCase().includes(searchLower)
    );
  });

  const [currentPage, setCurrentPage] = useState(1);
  const recordsPerPage = 8;
  const totalPages = Math.ceil(filteredClients.length / recordsPerPage);
  const paginatedClients = filteredClients.slice(
    (currentPage - 1) * recordsPerPage,
    currentPage * recordsPerPage
  );

  // Reset to page 1 when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);
  const [showProfile, setShowProfile] = useState(false);
  const [profilePopoverOpen, setProfilePopoverOpen] = useState(false);
  const [profileAnchorEl, setProfileAnchorEl] = useState(null);
  const [showReportForm, setShowReportForm] = useState(false);
  const [reportClient, setReportClient] = useState(null);
  const [showReportsViewer, setShowReportsViewer] = useState(false);
  const [viewReportsClient, setViewReportsClient] = useState(null);
  const [showDropdownMenu, setShowDropdownMenu] = useState({});
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferClient, setTransferClient] = useState(null);
  const [transferType, setTransferType] = useState('');
  const [showEntrepreneurDashboard, setShowEntrepreneurDashboard] = useState(false);
  const [selectedEntrepreneurClient, setSelectedEntrepreneurClient] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState({
    isOpen: false,
    client: null,
  });
  const [isDeleting, setIsDeleting] = useState(false);
  const [blockingClient, setBlockingClient] = useState(null);
  const [blockConfirm, setBlockConfirm] = useState({
    isOpen: false,
    client: null,
  });

  useEffect(() => {
    fetchClients();
    fetchActiveModerators();
    const savedIndex = localStorage.getItem('lastAssignedModeratorIndex');
    if (savedIndex) {
      setLastAssignedModeratorIndex(parseInt(savedIndex));
    }
  }, []);

  const toggleDropdownMenu = clientId => {
    setShowDropdownMenu(prev => {
      const newState = {};
      if (!prev[clientId]) {
        newState[clientId] = true;
      }
      return newState;
    });
  };

  const closeAllDropdownMenus = () => {
    setShowDropdownMenu({});
  };

  useEffect(() => {
    const handleClickOutside = event => {
      if (
        !event.target.closest('.dropdown-container') &&
        !event.target.closest('.fixed.inset-0.z-40')
      ) {
        closeAllDropdownMenus();
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const showToast = (message, type = 'info') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchActiveModerators = async () => {
    try {
      const snapshot = await getDocs(
        query(collection(firestore, 'moderator'), where('status', '==', 'active'))
      );
      const data = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setModerators(data);
    } catch (err) {
      toast.error('Failed to fetch moderators:', err);
    }
  };

  const getNextModerator = () => {
    if (moderators.length === 0) return null;

    const nextIndex = (lastAssignedModeratorIndex + 1) % moderators.length;
    setLastAssignedModeratorIndex(nextIndex);
    localStorage.setItem('lastAssignedModeratorIndex', nextIndex.toString());
    return moderators[nextIndex];
  };

  const fetchClients = async () => {
    setLoading(true);
    try {
      const snapshot = await getDocs(collection(firestore, 'clients'));
      const data = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        businessStage: normalizeBusinessStage(doc.data().businessStage),
      }));
      setClients(data);
      if (data.length === 0) showToast('No clients found', 'info');
    } catch (err) {
      setError('Failed to fetch clients');
      showToast('Failed to load clients', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleView = async client => {
    setSelectedClient(client);
    setShowModal(true);
  };

  const handleDeleteClick = client => {
    setDeleteConfirm({
      isOpen: true,
      client,
    });
  };

  const handleBlockClient = client => {
    setBlockConfirm({
      isOpen: true,
      client,
    });
  };

  const handleBlockConfirm = async () => {
    if (!blockConfirm.client) return;

    const client = blockConfirm.client;
    setBlockingClient(client.id);
    try {
      const updateData = {
        isBlocked: !client.isBlocked,
        updatedAt: new Date(),
      };

      if (!client.isBlocked) {
        updateData.blockedAt = new Date().toISOString();
        updateData.blockedBy = currentUser?.email || userRole || 'admin';
      } else {
        updateData.blockedAt = null;
        updateData.blockedBy = null;
      }

      await updateDoc(doc(firestore, 'clients', client.id), updateData);
      showToast(
        `Client account ${!client.isBlocked ? 'blocked' : 'unblocked'} successfully`,
        'success'
      );
      setBlockConfirm({ isOpen: false, client: null });
      fetchClients();
    } catch (err) {
      showToast(
        `Failed to ${!client.isBlocked ? 'block' : 'unblock'} client: ${err.message}`,
        'error'
      );
    } finally {
      setBlockingClient(null);
    }
  };

  const handleBlockCancel = () => {
    setBlockConfirm({ isOpen: false, client: null });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteConfirm.client) return;

    setIsDeleting(true);
    try {
      // Get the full client data
      const clientDoc = await getDoc(doc(firestore, 'clients', deleteConfirm.client.id));
      const clientData = clientDoc.data();

      // Add deletion metadata
      const deletedUserData = {
        ...clientData,
        originalCollection: 'clients',
        deletedAt: new Date().toISOString(),
        deletedBy: userRole || 'admin',
      };

      // Move to deleted_users collection
      await setDoc(doc(firestore, 'deleted_users', deleteConfirm.client.id), deletedUserData);

      // Delete from clients collection
      await deleteDoc(doc(firestore, 'clients', deleteConfirm.client.id));

      // Also delete from applications if exists
      try {
        await deleteDoc(doc(firestore, 'applications', deleteConfirm.client.id));
      } catch (err) {}

      showToast('Client deleted and moved to deleted users!', 'success');
      setDeleteConfirm({ isOpen: false, client: null });
      fetchClients();
    } catch (err) {
      showToast(`Failed to delete client: ${err.message}`, 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirm({ isOpen: false, client: null });
  };

  const handleCreate = () => {
    setShowAddClientForm(true);
  };

  const handleAddClientSubmit = async result => {
    setShowAddClientForm(false);
    try {
      if (result.success) {
        showToast(`Client created successfully! Password: ${result.password}`, 'success');
      } else {
        showToast('Client created but there was an issue', 'info');
      }
      fetchClients();
    } catch (err) {
      showToast(err.message || 'Failed to create client', 'error');
    }
  };

  const handleMakeAlumni = async client => {
    try {
      await deleteDoc(doc(firestore, 'applications', client.id));
      await deleteDoc(doc(firestore, 'clients', client.id));
      await setDoc(doc(firestore, 'alumni', client.id), { ...client, role: 'alumni' });
      showToast('Client converted to alumni', 'success');
      fetchClients();
    } catch (err) {
      showToast('Failed to convert client to alumni', 'error');
    }
  };

  const handleOpenProfilePopover = (event, client) => {
    if (event) {
      setProfileAnchorEl(event.currentTarget);
    } else {
      setProfileAnchorEl(null);
    }
    setSelectedClient(client);
    setProfilePopoverOpen(true);
  };

  const handleOpenEntrepreneurDashboard = client => {
    setSelectedEntrepreneurClient(client);
    setShowEntrepreneurDashboard(true);
  };

  const handleCloseProfilePopover = () => {
    setProfilePopoverOpen(false);
    setProfileAnchorEl(null);
  };

  const handleCloseEntrepreneurDashboard = () => {
    setShowEntrepreneurDashboard(false);
    setSelectedEntrepreneurClient(null);
  };

  const handleSubmitReport = client => {
    setReportClient(client);
    setShowReportForm(true);
  };

  const handleCloseReportForm = () => {
    setShowReportForm(false);
    setReportClient(null);
  };

  const handleViewReports = client => {
    setViewReportsClient(client);
    setShowReportsViewer(true);
  };

  const handleCloseReportsViewer = () => {
    setShowReportsViewer(false);
    setViewReportsClient(null);
  };

  const handleTransfer = (client, type) => {
    setTransferClient(client);
    setTransferType(type);
    setShowTransferModal(true);
    closeAllDropdownMenus();
  };

  const handleCloseTransferModal = () => {
    setShowTransferModal(false);
    setTransferClient(null);
    setTransferType('');
    fetchClients();
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="mx-auto max-w-7xl">
        <div className="mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-black">Client Management</h1>
            </div>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleCreate}
              className="flex items-center gap-2 rounded-lg bg-blue-600 px-4 py-2 text-white shadow-sm transition-colors hover:bg-blue-700"
            >
              <HiPlus className="h-5 w-5" />
              Add New Client
            </motion.button>
          </div>
        </div>
        <AnimatePresence>
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="mb-6 rounded-lg border border-red-200 bg-red-50 p-4"
            >
              <div className="flex items-center gap-2">
                <HiExclamationTriangle className="h-5 w-5 text-red-500" />
                <span className="text-red-700">{error}</span>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Search Bar */}
        <div className="mb-6 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="relative flex-1">
              <div className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2">
                <svg
                  className="h-5 w-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  />
                </svg>
              </div>
              <input
                type="text"
                placeholder="Search by name, email, organization, phone, or business stage..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                className="w-full rounded-lg border border-gray-300 py-2.5 pl-10 pr-10 text-sm text-black placeholder-gray-400 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-500/20"
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                  aria-label="Clear search"
                >
                  <HiXMark className="h-4 w-4" />
                </button>
              )}
            </div>
            {searchTerm && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <span className="font-medium text-black">{filteredClients.length}</span>
                <span>result{filteredClients.length !== 1 ? 's' : ''} found</span>
              </div>
            )}
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
          {loading ? (
            <div className="p-8 text-center">
              <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-blue-500 border-t-transparent"></div>
              <p className="mt-2 text-gray-600">Loading clients...</p>
            </div>
          ) : filteredClients.length === 0 ? (
            <div className="p-8 text-center">
              <HiUser className="mx-auto h-12 w-12 text-gray-400" />
              <h3 className="mt-2 text-lg font-medium text-black">
                {searchTerm ? 'No clients found matching your search' : 'No clients found'}
              </h3>
              <p className="mt-1 text-gray-600">
                {searchTerm
                  ? 'Try adjusting your search terms or clear the search to see all clients.'
                  : 'Get started by adding your first client.'}
              </p>
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="mt-4 rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-blue-700"
                >
                  Clear Search
                </button>
              )}
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      <div className="flex items-center gap-2">
                        <HiUser className="h-4 w-4" /> Name
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      <div className="flex items-center gap-2">
                        <HiEnvelope className="h-4 w-4" /> Email
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      <div className="flex items-center gap-2">
                        <HiPhone className="h-4 w-4" /> Phone
                      </div>
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                      <div className="flex items-center gap-2">
                        <HiBuildingOffice2 className="h-4 w-4" /> Stage
                      </div>
                    </th>
                    <th className="px-6 py-3 text-center text-xs font-medium uppercase tracking-wider text-gray-500">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {paginatedClients.map((client, index) => (
                    <motion.tr
                      key={client.id}
                      initial={{ opacity: 0, y: 10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -10 }}
                      transition={{ duration: 0.25, ease: 'easeInOut' }}
                      className="hover:bg-gray-50"
                    >
                      <td className="whitespace-nowrap px-6 py-4">
                        <div className="flex items-center">
                          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-blue-600 text-white">
                            {(client.organization ||
                              client.email ||
                              client.id)?.[0]?.toUpperCase() || 'C'}
                          </div>
                          <div className="ml-4">
                            <div className="text-sm font-medium text-black">
                              {client.firstname || ''} {client.lastname || ''}
                            </div>
                            <div className="text-sm text-gray-500">
                              {client.organization || 'No organization'}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4">
                        <div className="text-sm text-black">{client.email}</div>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4">
                        <div className="text-sm text-black">{client.phone}</div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex flex-col gap-2">
                          {(() => {
                            const stage = client.businessStage;
                            const c = BUSINESS_STAGE_COLORS[stage] || {
                              bg: 'bg-gray-100',
                              text: 'text-gray-700',
                            };
                            return (
                              <span
                                className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${c.bg} ${c.text}`}
                              >
                                {stage || '-'}
                              </span>
                            );
                          })()}
                          {client.isBlocked && (
                            <span className="inline-flex items-center gap-1 rounded-full bg-red-100 px-2.5 py-1 text-xs font-medium text-red-700">
                              <HiLockClosed className="h-3 w-3" />
                              Blocked
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="whitespace-nowrap px-6 py-4 text-sm font-medium">
                        <div className="dropdown-container relative">
                          <motion.button
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                            onClick={() => toggleDropdownMenu(client.id)}
                            className="rounded-lg bg-gray-100 p-2 text-gray-600 transition-colors hover:bg-gray-200"
                            title="Actions"
                          >
                            <HiEllipsisVertical className="h-4 w-4" />
                          </motion.button>

                          {showDropdownMenu[client.id] && (
                            <>
                              <motion.div
                                initial={{ opacity: 0 }}
                                animate={{ opacity: 1 }}
                                exit={{ opacity: 0 }}
                                onClick={() =>
                                  setShowDropdownMenu(prev => ({ ...prev, [client.id]: false }))
                                }
                                className="fixed inset-0 z-40 bg-black/20"
                              />

                              <motion.div
                                initial={{ opacity: 0, scale: 0.95, y: -10 }}
                                animate={{ opacity: 1, scale: 1, y: 0 }}
                                exit={{ opacity: 0, scale: 0.95, y: -10 }}
                                className="fixed z-50 w-56 rounded-lg border border-gray-200 bg-white shadow-xl"
                                style={{
                                  position: 'fixed',
                                  top: '100px',
                                  right: '20px',
                                  zIndex: 9999,
                                  maxHeight: '400px',
                                  overflowY: 'auto',
                                }}
                              >
                                <div className="py-1">
                                  {/* View Profile */}
                                  <button
                                    onClick={() => {
                                      handleOpenEntrepreneurDashboard(client);
                                      setShowDropdownMenu(prev => ({
                                        ...prev,
                                        [client.id]: false,
                                      }));
                                    }}
                                    className="flex w-full items-center gap-3 px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                                  >
                                    <HiUser className="h-4 w-4 text-blue-600" />
                                    View Full Profile
                                  </button>

                                  {/* View Details */}
                                  <button
                                    onClick={() => {
                                      handleView(client);
                                      setShowDropdownMenu(prev => ({
                                        ...prev,
                                        [client.id]: false,
                                      }));
                                    }}
                                    className="flex w-full items-center gap-3 px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                                  >
                                    <HiEye className="h-4 w-4 text-gray-600" />
                                    View Details
                                  </button>

                                  {/* Admin/Moderator/Coach Actions */}
                                  {['admin', 'Business Advisor', 'coach'].includes(userRole) && (
                                    <>
                                      {/* Open Document Bank */}
                                      <button
                                        onClick={() => {
                                          window.dispatchEvent(
                                            new CustomEvent('open-document-bank', {
                                              detail: { client },
                                            })
                                          );
                                          setShowDropdownMenu(prev => ({
                                            ...prev,
                                            [client.id]: false,
                                          }));
                                        }}
                                        className="flex w-full items-center gap-3 px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                                      >
                                        <HiDocumentText className="h-4 w-4 text-blue-600" />
                                        Open Document Bank
                                      </button>

                                      {/* Submit Report */}
                                      <button
                                        onClick={() => {
                                          handleSubmitReport(client);
                                          setShowDropdownMenu(prev => ({
                                            ...prev,
                                            [client.id]: false,
                                          }));
                                        }}
                                        className="flex w-full items-center gap-3 px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                                      >
                                        <HiDocumentText className="h-4 w-4 text-purple-600" />
                                        Submit Report
                                      </button>

                                      {/* View Reports */}
                                      <button
                                        onClick={() => {
                                          handleViewReports(client);
                                          setShowDropdownMenu(prev => ({
                                            ...prev,
                                            [client.id]: false,
                                          }));
                                        }}
                                        className="flex w-full items-center gap-3 px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                                      >
                                        <HiClipboardDocumentList className="h-4 w-4 text-indigo-600" />
                                        View Reports
                                      </button>
                                    </>
                                  )}

                                  {/* Admin-Only Transfer Actions */}
                                  {userRole === 'admin' && (
                                    <>
                                      {/* Divider */}
                                      <div className="my-1 border-t border-gray-100"></div>

                                      {/* Transfer Coach */}
                                      <button
                                        onClick={() => {
                                          handleTransfer(client, 'coach');
                                        }}
                                        className="flex w-full items-center gap-3 px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                                      >
                                        <HiAcademicCap className="h-4 w-4 text-green-600" />
                                        Transfer Coach
                                      </button>

                                      <button
                                        onClick={() => {
                                          handleTransfer(client, 'moderator');
                                        }}
                                        className="flex w-full items-center gap-3 px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                                      >
                                        <HiUserGroup className="h-4 w-4 text-blue-600" />
                                        Transfer Business Advisor
                                      </button>
                                    </>
                                  )}

                                  {/* Block/Unblock Account */}
                                  {['admin', 'Business Advisor', 'coach'].includes(userRole) && (
                                    <>
                                      {/* Divider */}
                                      <div className="my-1 border-t border-gray-100"></div>

                                      <button
                                        onClick={() => {
                                          handleBlockClient(client);
                                          setShowDropdownMenu(prev => ({
                                            ...prev,
                                            [client.id]: false,
                                          }));
                                        }}
                                        disabled={blockingClient === client.id}
                                        className={`flex w-full items-center gap-3 px-4 py-2 text-left text-sm hover:bg-gray-50 ${
                                          client.isBlocked ? 'text-green-700' : 'text-red-700'
                                        } ${blockingClient === client.id ? 'cursor-not-allowed opacity-50' : ''}`}
                                      >
                                        {client.isBlocked ? (
                                          <>
                                            <HiLockOpen className="h-4 w-4 text-green-600" />
                                            Unblock Account
                                          </>
                                        ) : (
                                          <>
                                            <HiLockClosed className="h-4 w-4 text-red-600" />
                                            Block Account
                                          </>
                                        )}
                                      </button>
                                    </>
                                  )}

                                  {/* Divider */}
                                  <div className="my-1 border-t border-gray-100"></div>

                                  {/* Make Alumni */}
                                  <button
                                    onClick={() => {
                                      handleMakeAlumni(client);
                                      setShowDropdownMenu(prev => ({
                                        ...prev,
                                        [client.id]: false,
                                      }));
                                    }}
                                    className="flex w-full items-center gap-3 px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-50"
                                  >
                                    <HiAcademicCap className="h-4 w-4 text-green-600" />
                                    Make Alumni
                                  </button>

                                  {/* Delete Client */}
                                  <button
                                    onClick={() => {
                                      handleDeleteClick(client);
                                      setShowDropdownMenu(prev => ({
                                        ...prev,
                                        [client.id]: false,
                                      }));
                                    }}
                                    className="flex w-full items-center gap-3 px-4 py-2 text-left text-sm text-red-700 hover:bg-red-50"
                                  >
                                    <HiTrash className="h-4 w-4 text-red-600" />
                                    Delete Client
                                  </button>
                                </div>
                              </motion.div>
                            </>
                          )}
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
              {/* Pagination Controls */}
              {totalPages > 1 && (
                <div className="flex items-center justify-end gap-2 px-6 py-4">
                  <button
                    className={`rounded border px-2 py-1 text-sm font-medium transition-colors ${currentPage === 1 ? 'cursor-not-allowed border-gray-200 bg-gray-50 text-gray-400' : 'border-gray-200 text-blue-600 hover:bg-blue-50'}`}
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    Prev
                  </button>
                  {Array.from({ length: totalPages }).map((_, i) => (
                    <button
                      key={i}
                      className={`rounded border px-2 py-1 text-sm font-medium transition-colors ${currentPage === i + 1 ? 'border-blue-600 bg-blue-600 text-white' : 'border-gray-200 text-gray-700 hover:bg-blue-50'}`}
                      onClick={() => setCurrentPage(i + 1)}
                    >
                      {i + 1}
                    </button>
                  ))}
                  <button
                    className={`rounded border px-2 py-1 text-sm font-medium transition-colors ${currentPage === totalPages ? 'cursor-not-allowed border-gray-200 bg-gray-50 text-gray-400' : 'border-gray-200 text-blue-600 hover:bg-blue-50'}`}
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Stats */}
        <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-5">
          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">
                  {searchTerm ? 'Filtered Clients' : 'Total Clients'}
                </p>
                <p className="text-2xl font-bold text-black">
                  {searchTerm ? filteredClients.length : clients.length}
                </p>
                {searchTerm && <p className="text-xs text-gray-500">of {clients.length} total</p>}
              </div>
              <div className="rounded-full bg-blue-100 p-3">
                <HiUser className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Business Stages</p>
                <p className="text-2xl font-bold text-black">
                  {new Set((searchTerm ? filteredClients : clients).map(c => c.businessStage)).size}
                </p>
              </div>
              <div className="rounded-full bg-purple-100 p-3">
                <HiBuildingOffice2 className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Active</p>
                <p className="text-2xl font-bold text-black">
                  {
                    (searchTerm ? filteredClients : clients).filter(
                      c => c.status === 'Active' && !c.isBlocked
                    ).length
                  }
                </p>
              </div>
              <div className="rounded-full bg-green-100 p-3">
                <HiCheckCircle className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Blocked/Deactivated</p>
                <p className="text-2xl font-bold text-black">
                  {(searchTerm ? filteredClients : clients).filter(c => c.isBlocked).length}
                </p>
              </div>
              <div className="rounded-full bg-red-100 p-3">
                <HiLockClosed className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">New This Month</p>
                <p className="text-2xl font-bold text-black">
                  {
                    (searchTerm ? filteredClients : clients).filter(c => {
                      const createdAt = c.createdAt?.toDate?.() || new Date(c.createdAt);
                      const now = new Date();
                      return (
                        createdAt.getMonth() === now.getMonth() &&
                        createdAt.getFullYear() === now.getFullYear()
                      );
                    }).length
                  }
                </p>
              </div>
              <div className="rounded-full bg-orange-100 p-3">
                <HiPlus className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </div>
        </div>
        <AnimatePresence>
          {showModal && selectedClient && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
            >
              <motion.div
                initial={{ opacity: 0, y: 50, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 50, scale: 0.95 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
                className="relative max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-gray-100 bg-gradient-to-br from-white to-blue-50 shadow-2xl md:max-w-2xl"
              >
                <div className="flex flex-col gap-4 rounded-t-2xl border-b border-gray-100 bg-white/80 px-6 py-4 md:flex-row md:items-center md:justify-between">
                  <div className="flex items-center gap-3">
                    <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-blue-600 text-lg font-bold text-white shadow-sm">
                      {(selectedClient.firstname ||
                        selectedClient.email ||
                        selectedClient.id)?.[0]?.toUpperCase() || 'C'}
                    </div>
                    <div>
                      <div className="flex flex-col gap-0.5">
                        <span className="text-base font-semibold text-black">
                          {selectedClient.firstname || ''} {selectedClient.lastname || ''}
                        </span>
                        <span className="text-xs text-gray-500">{selectedClient.email}</span>
                      </div>
                    </div>
                  </div>
                  <button
                    className="self-start rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 md:self-auto"
                    onClick={() => setShowModal(false)}
                    aria-label="Close"
                  >
                    <HiXMark className="h-6 w-6" />
                  </button>
                </div>
                <div className="space-y-8 p-6">
                  {/* Client Details Card */}
                  <div className="mb-2 text-sm font-semibold tracking-wide text-blue-900/80">
                    Client Details
                  </div>
                  <div className="mb-2 text-sm font-semibold tracking-wide text-blue-900/80">
                    {/*Open Client Profile Popover*/}
                    <button
                      className="rounded-lg bg-blue-50 px-3 py-1.5 text-sm font-medium text-blue-600 hover:bg-blue-100"
                      onClick={event => handleOpenProfilePopover(event, selectedClient)}
                    >
                      Open Profile
                    </button>
                  </div>
                  <div className="grid grid-cols-1 gap-4 rounded-xl border border-blue-100 bg-blue-50/60 p-4 md:grid-cols-2">
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <HiUser className="h-4 w-4 text-blue-500" /> First Name:{' '}
                        <span className="font-semibold text-black">
                          {selectedClient.firstname || '-'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <HiUser className="h-4 w-4 text-blue-500" /> Last Name:{' '}
                        <span className="font-semibold text-black">
                          {selectedClient.lastname || '-'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <HiEnvelope className="h-4 w-4 text-blue-500" /> Email:{' '}
                        <span className="font-semibold text-black">
                          {selectedClient.email || '-'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <HiPhone className="h-4 w-4 text-blue-500" /> Phone:{' '}
                        <span className="font-semibold text-black">
                          {selectedClient.phone || '-'}
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-col gap-2">
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <HiBuildingOffice2 className="h-4 w-4 text-blue-500" /> Organization:{' '}
                        <span className="font-semibold text-black">
                          {selectedClient.organization || '-'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <HiBuildingOffice2 className="h-4 w-4 text-blue-500" /> Business Stage:{' '}
                        <span className="font-semibold text-black">
                          {selectedClient.businessStage || '-'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        <HiCheckCircle className="h-4 w-4 text-blue-500" /> Status:{' '}
                        <span className="font-semibold text-black">
                          {selectedClient.status || '-'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-gray-700">
                        {selectedClient.isBlocked ? (
                          <>
                            <HiLockClosed className="h-4 w-4 text-red-500" /> Account Status:{' '}
                            <span className="font-semibold text-red-600">Blocked/Deactivated</span>
                          </>
                        ) : (
                          <>
                            <HiLockOpen className="h-4 w-4 text-green-500" /> Account Status:{' '}
                            <span className="font-semibold text-green-600">Active</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                  {/* Advisor & Coach Section */}
                  <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                    {/* Advisor Section */}
                    <div className="flex flex-col gap-2 rounded-lg border border-blue-100 bg-blue-50/40 p-3">
                      <div className="mb-1 flex items-center gap-2">
                        <HiUserGroup className="h-4 w-4 text-blue-600" />
                        <span className="text-xs font-semibold uppercase tracking-wider text-blue-900/80">
                          Business Advisor
                        </span>
                      </div>
                      <Detail
                        label="Name"
                        value={selectedClient.assigned || '-'}
                        icon={<HiUser className="h-4 w-4" />}
                      />
                      <Detail
                        label="Email"
                        value={selectedClient.assignedEmail || '-'}
                        icon={<HiEnvelope className="h-4 w-4" />}
                      />
                    </div>
                    {/* Coach Section */}
                    <div className="flex flex-col gap-2 rounded-lg border border-green-100 bg-green-50/40 p-3">
                      <div className="mb-1 flex items-center gap-2">
                        <HiAcademicCap className="h-4 w-4 text-green-600" />
                        <span className="text-xs font-semibold uppercase tracking-wider text-green-900/80">
                          Assigned Coach
                        </span>
                      </div>
                      <Detail
                        label="Name"
                        value={selectedClient.assignedCoachName || '-'}
                        icon={<HiUser className="h-4 w-4" />}
                      />
                      <Detail
                        label="Email"
                        value={selectedClient.assignedCoachEmail || '-'}
                        icon={<HiEnvelope className="h-4 w-4" />}
                      />
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {showAddClientForm && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
            >
              <motion.div
                initial={{ opacity: 0, y: 50, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 50, scale: 0.95 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
                className="relative w-full max-w-2xl overflow-y-auto rounded-2xl bg-white shadow-2xl"
              >
                <AddClientForm
                  open={showAddClientForm}
                  onClose={() => setShowAddClientForm(false)}
                  onSubmit={handleAddClientSubmit}
                  onError={error => showToast(error.message || 'Failed to create client', 'error')}
                />
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        <AnimatePresence>
          {toast && (
            <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
          )}
        </AnimatePresence>

        <ClientProfilePopover
          isOpen={profilePopoverOpen}
          onClose={handleCloseProfilePopover}
          client={selectedClient}
          anchorEl={profileAnchorEl}
          currentUser={currentUser}
          userRole={userRole}
        />

        <ReportSubmissionForm
          isOpen={showReportForm}
          onClose={handleCloseReportForm}
          client={reportClient}
          currentUser={currentUser}
          userRole={userRole}
        />

        <ClientReportsViewer
          isOpen={showReportsViewer}
          onClose={handleCloseReportsViewer}
          client={viewReportsClient}
          userRole={userRole}
        />

        <TransferModal
          isOpen={showTransferModal}
          onClose={handleCloseTransferModal}
          client={transferClient}
          currentUser={currentUser}
          transferType={transferType}
        />
        <AnimatePresence>
          {showEntrepreneurDashboard && selectedEntrepreneurClient && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
            >
              <motion.div
                initial={{ opacity: 0, y: 50, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 50, scale: 0.95 }}
                transition={{ duration: 0.2, ease: 'easeOut' }}
                className="relative h-[90vh] w-full max-w-7xl overflow-hidden rounded-2xl bg-white shadow-2xl"
              >
                <div className="flex items-center justify-between border-b border-gray-200 bg-white px-6 py-4">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-blue-100">
                      <HiUser className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <h2 className="text-lg font-semibold text-black">Client Profile</h2>
                      <p className="text-sm text-gray-500">
                        {selectedEntrepreneurClient.firstname} {selectedEntrepreneurClient.lastname}{' '}
                        - {selectedEntrepreneurClient.organization}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleCloseEntrepreneurDashboard}
                    className="rounded-lg p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                  >
                    <HiXMark className="h-6 w-6" />
                  </button>
                </div>

                <div className="h-full overflow-y-auto">
                  <EntrepreneurDashboard
                    clientData={selectedEntrepreneurClient}
                    isReadOnly={true}
                  />
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        <ConfirmationModal
          isOpen={deleteConfirm.isOpen}
          onClose={handleDeleteCancel}
          onConfirm={handleDeleteConfirm}
          title="Delete Client"
          message={`Are you sure you want to delete ${deleteConfirm.client?.firstname || ''} ${deleteConfirm.client?.lastname || ''}? They will be moved to deleted users.`}
          confirmText="Delete"
          cancelText="Cancel"
          confirmButtonColor="bg-red-600 hover:bg-red-700"
          isLoading={isDeleting}
        />

        <ConfirmationModal
          isOpen={blockConfirm.isOpen}
          onClose={handleBlockCancel}
          onConfirm={handleBlockConfirm}
          title={blockConfirm.client?.isBlocked ? 'Unblock Account' : 'Block Account'}
          message={`Are you sure you want to ${blockConfirm.client?.isBlocked ? 'unblock' : 'block'} ${blockConfirm.client?.firstname || ''} ${blockConfirm.client?.lastname || ''}'s account? ${blockConfirm.client?.isBlocked ? 'They will be able to login again.' : 'They will not be able to login until unblocked.'}`}
          confirmText={blockConfirm.client?.isBlocked ? 'Unblock' : 'Block'}
          cancelText="Cancel"
          confirmButtonColor={
            blockConfirm.client?.isBlocked
              ? 'bg-green-600 hover:bg-green-700'
              : 'bg-red-600 hover:bg-red-700'
          }
          isLoading={blockingClient === blockConfirm.client?.id}
        />
      </div>
    </div>
  );
};

const Detail = ({ label, value, icon }) => (
  <div className="flex items-center gap-3 py-1">
    {icon && <span className="text-blue-500">{icon}</span>}
    <div className="flex flex-col">
      <span className="text-xs font-medium text-gray-500">{label}</span>
      <span className="min-w-0 rounded border border-gray-100 bg-gray-50 px-3 py-1 text-sm text-black sm:min-w-[120px]">
        {value || '-'}
      </span>
    </div>
  </div>
);

export default ClientsTable;
